// 권역 경계 · 권역 · 커버리지 · 케이스 동선 오버레이.
//
// 렌더링 철학: 지도는 "지금 누가 어떤 서비스를 진행 중인지" 한눈에 보는 뷰다.
// 경계/권역은 뒤로 물러난 조용한 프레임 — 실 서비스 시그널(마커/동선/상태)이
// 앞에 오도록 stroke는 얇게 점선으로, fill은 최소로 유지한다.
// 예외: 시(市) 외곽선은 굵은 점선으로 "여기가 김해시 전역" 을 각인.

import type { BBox, Coord } from "./types";
import type { LatLng, MultiPolygonPath } from "./pilotBoundary";

const ORANGE_500 = "#E8825B";
const ORANGE_600 = "#D26A44";
// 외곽선 톤 — 빨간 실선으로 시(市) 경계 강조.
const OUTLINE_INK = "#DC2626";

// ── Boundary (bbox 사각형 — 김해 외 파일럿 권역) ─────────────
export function createBoundary(naver: any, map: any, bbox: BBox): any {
  return new naver.maps.Rectangle({
    map,
    bounds: new naver.maps.LatLngBounds(
      new naver.maps.LatLng(bbox.south, bbox.west),
      new naver.maps.LatLng(bbox.north, bbox.east),
    ),
    strokeColor:   ORANGE_500,
    strokeStyle:   "shortdash",
    strokeWeight:  2,
    strokeOpacity: 0.75,
    fillColor:     ORANGE_500,
    fillOpacity:   0.04,
    clickable:     false,
  });
}

export function isBoundaryDrawable(bbox: BBox | null | undefined): bbox is BBox {
  return !!bbox && bbox.north !== bbox.south && bbox.east !== bbox.west;
}

// ── 헬퍼: LatLng 변환 ─────────────────────────────────────────
function toLatLngRing(naver: any, ring: LatLng[]): any[] {
  return ring.map(([lat, lng]) => new naver.maps.LatLng(lat, lng));
}
function toPolygonPaths(naver: any, mp: MultiPolygonPath): any[][] {
  // Naver Polygon.paths 는 링 배열([[ring1], [ring2]…]) 형식으로 홀 지원.
  // MultiPolygon은 폴리곤별로 개별 Polygon 인스턴스를 만든다(SDK 호환성).
  // 이 헬퍼는 "하나의 폴리곤"에 대한 paths(=rings) 만 반환.
  return mp.length ? mp[0].map(r => toLatLngRing(naver, r)) : [];
}

// ── 시(市) 외곽선 — 굵은 점선, fill 0, 지도 위 플로팅 프레임 ──
// MultiPolygon 이면 폴리곤마다 Naver Polygon 을 만들어 배열로 반환.
// (김해 외곽은 단일 Polygon 이지만 API 통일성을 위해 배열 반환.)
export function createOuterPolygons(naver: any, map: any, mp: MultiPolygonPath): any[] {
  const outers: any[] = [];
  for (const poly of mp) {
    outers.push(new naver.maps.Polygon({
      map,
      paths: poly.map(r => toLatLngRing(naver, r)),
      strokeColor:   OUTLINE_INK,
      strokeStyle:   "solid",    // 실선
      strokeWeight:  3,
      strokeOpacity: 0.95,
      fillColor:     OUTLINE_INK,
      fillOpacity:   0,          // 채움 없음 — 프레임만
      clickable:     false,
      zIndex:        50,         // 마커(100+)보단 뒤, 권역(20)보단 앞
    }));
  }
  return outers;
}

// ── 권역 폴리곤 ─────────────────────────────────────────────
export type SubAreaVisualState = "default" | "selected" | "muted";

// 시각 상태별 stroke/fill.
// 3권역은 각자 다른 색 실선 테두리 — 지도 위에서 "여기부터 저기까지가 북부" 가
// 즉시 읽혀야 하므로 default 도 solid + 도드라진 opacity. 시(市) 외곽(빨간 실선
// weight 3)과는 색으로 분리된다.
// - default:  각 권역 색 실선(2px) + 아주 옅은 fill (아무 선택 없을 때 3개 모두 보임)
// - selected: 같은 색 굵은 실선(4px) + 진한 fill — 선택 시 해당 권역만 도드라짐
// - muted:    사실상 숨김(투명) — 다른 권역 선택 시 시선을 뺏지 않도록 완전히 배경화
export function subAreaOptionsFor(color: string, state: SubAreaVisualState) {
  if (state === "selected") {
    return {
      strokeColor: color, strokeStyle: "solid" as const, strokeWeight: 4,
      strokeOpacity: 1, fillColor: color, fillOpacity: 0.22,
    };
  }
  if (state === "muted") {
    return {
      strokeColor: color, strokeStyle: "solid" as const, strokeWeight: 0,
      strokeOpacity: 0, fillColor: color, fillOpacity: 0,
    };
  }
  return {
    strokeColor: color, strokeStyle: "solid" as const, strokeWeight: 2,
    strokeOpacity: 0.8, fillColor: color, fillOpacity: 0.05,
  };
}

// MultiPolygon → Polygon 인스턴스 배열.
export function createSubAreaPolygons(
  naver: any, map: any, mp: MultiPolygonPath, color: string, state: SubAreaVisualState,
): any[] {
  const opts = subAreaOptionsFor(color, state);
  return mp.map(poly => new naver.maps.Polygon({
    map,
    paths: poly.map(r => toLatLngRing(naver, r)),
    ...opts,
    clickable: false,
    zIndex: 20,
  }));
}

// 한 권역의 모든 폴리곤 옵션 일괄 갱신.
export function updateSubAreaPolygons(polys: any[], color: string, state: SubAreaVisualState): void {
  const opts = subAreaOptionsFor(color, state);
  for (const p of polys) {
    try { p.setOptions(opts); } catch { /* SDK 버전차 흡수 */ }
  }
}

// ── 커버리지 원 (복지관 서비스 반경) ─────────────────────────
export function coverageOptionsFor(color: string, state: SubAreaVisualState) {
  if (state === "selected") {
    return {
      strokeColor: color, strokeStyle: "solid" as const, strokeWeight: 2,
      strokeOpacity: 0.85, fillColor: color, fillOpacity: 0.10,
    };
  }
  if (state === "muted") {
    // 선택된 권역에 집중하도록 다른 권역 커버리지는 완전히 숨김.
    return {
      strokeColor: color, strokeStyle: "solid" as const, strokeWeight: 0,
      strokeOpacity: 0, fillColor: color, fillOpacity: 0,
    };
  }
  return {
    strokeColor: color, strokeStyle: "shortdash" as const, strokeWeight: 1.2,
    strokeOpacity: 0.4, fillColor: color, fillOpacity: 0.04,
  };
}

export function createCoverageCircle(
  naver: any, map: any, center: LatLng, radius_m: number, color: string, state: SubAreaVisualState,
): any {
  return new naver.maps.Circle({
    map,
    center: new naver.maps.LatLng(center[0], center[1]),
    radius: radius_m,
    ...coverageOptionsFor(color, state),
    clickable: false,
    zIndex: 15,
  });
}

export function updateCoverageCircle(circle: any, color: string, state: SubAreaVisualState): void {
  try { circle.setOptions(coverageOptionsFor(color, state)); } catch { /* SDK 버전차 흡수 */ }
}

// ── 거점 앵커 마커 — 배지 + 라벨 pill ─────────────────────
// [[project_welfare_anchor.md]] — 3권역은 거점 시설을 앵커로 하므로
// 지도에서 다른 마커보다 우선 눈에 들어와야 함.
// kind: "welfare"  → 비장기요양(사회복지관) — house+ 아이콘, 흰 pill
// kind: "longterm" → 장기요양기관(사회복지법인) — heart+shield 아이콘, 색 pill(테)
// 배지("거점" 또는 "장기") + 글로우 링으로 일반 마커/기관 마커와 확실히 분리.
export type AnchorKind = "welfare" | "longterm";

export function createAnchorMarker(
  naver: any, map: any, center: LatLng, name: string, color: string, kind: AnchorKind = "welfare",
): any {
  const isLongterm = kind === "longterm";
  const badgeLabel = isLongterm ? "장기요양 거점" : "비장기 거점";
  // 아이콘: 복지관은 집(+표식), 장기요양은 방패(+심장) — 시설 성격 즉시 구분.
  const iconSvg = isLongterm
    ? `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
         <path d="M12 3 L4 6 V12 C4 16.5 7.4 19.8 12 21 C16.6 19.8 20 16.5 20 12 V6 Z"/>
         <path d="M12 10 V15 M9.5 12.5 H14.5"/>
       </svg>`
    : `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
         <path d="M3 11 L12 4 L21 11 V20 A1 1 0 0 1 20 21 H4 A1 1 0 0 1 3 20 Z"/>
         <path d="M12 13 V17 M10 15 H14"/>
       </svg>`;
  const html = `
    <div style="position:relative; transform:translate(-50%, -100%); pointer-events:none; font-family:'Pretendard','Apple SD Gothic Neo',sans-serif;">
      <div style="
        position:absolute; top:-8px; left:50%; transform:translateX(-50%);
        padding:2px 8px; border-radius:999px;
        background:${color}; color:#ffffff;
        font-size:9.5px; font-weight:800; letter-spacing:0.02em;
        box-shadow:0 2px 5px rgba(20,25,40,0.22);
        white-space:nowrap; z-index:2;
      ">${badgeLabel}</div>
      <div style="
        display:flex; align-items:center; justify-content:center;
        width:44px; height:44px; border-radius:${isLongterm ? '12px' : '50%'};
        background:${color};
        border:3.5px solid #ffffff;
        box-shadow:0 0 0 4px ${color}33, 0 6px 16px rgba(20,25,40,0.32), 0 2px 4px rgba(0,0,0,0.14);
        margin:8px auto 0;
      ">${iconSvg}</div>
      <div style="
        margin-top:7px;
        padding:3px 10px;
        border-radius:999px;
        background:#ffffff;
        color:#20293a;
        font-size:11.5px; font-weight:700; letter-spacing:-0.01em;
        text-align:center;
        white-space:nowrap;
        box-shadow:0 3px 8px rgba(20,25,40,0.18);
        border:${isLongterm ? `1.5px solid ${color}` : '1px solid rgba(20,25,40,0.06)'};
      ">${escapeHtml(name)}</div>
    </div>`;
  return new naver.maps.Marker({
    map,
    position: new naver.maps.LatLng(center[0], center[1]),
    icon: {
      content: html,
      anchor: new naver.maps.Point(0, 0), // HTML 내부에서 translate로 정렬
    },
    zIndex: 300, // 클러스터/마커(100+)보다 확실히 위
    clickable: false,
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!));
}

// ── Case links (Polyline) ────────────────────────────────────────────
export function createCaseLink(
  naver: any, map: any, ePoint: Coord, mPoint: Coord, highlighted: boolean, dimOthers: boolean = false,
): any {
  return new naver.maps.Polyline({
    map,
    path: [
      new naver.maps.LatLng(ePoint.lat, ePoint.lng),
      new naver.maps.LatLng(mPoint.lat, mPoint.lng),
    ],
    ...caseLinkStrokeOptions(highlighted, dimOthers),
    clickable: false,
  });
}

export function updateCaseLink(line: any, highlighted: boolean, dimOthers: boolean = false): void {
  try { line.setOptions(caseLinkStrokeOptions(highlighted, dimOthers)); } catch { /* SDK 버전차 흡수 */ }
}

// dimOthers: 어떤 대상이 선택된 상태에서 그 대상과 무관한 케이스 라인은 시선을
// 뺏지 않도록 훨씬 흐리게 그린다. 선택 없을 때는 기본 opacity 유지.
function caseLinkStrokeOptions(highlighted: boolean, dimOthers: boolean = false) {
  const dimmed = dimOthers && !highlighted;
  return {
    strokeColor:   highlighted ? ORANGE_600 : ORANGE_500,
    strokeStyle:   "shortdash" as const,
    strokeWeight:  highlighted ? 2.5 : 1.5,
    strokeOpacity: highlighted ? 0.9 : (dimmed ? 0.10 : 0.32),
  };
}
